import React, { useEffect, useMemo, useState } from "react";
import { client, STATUS_META, fmtTimeAST, msToCountdown, inServiceWindow } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast, Toaster } from "sonner";
import { AlertTriangle, Clock, GripVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { JobTimesDialog } from "@/components/JobTimesDialog";
import { JobReviewDialog } from "@/components/JobReviewDialog";
import { EditJobDialog } from "@/components/EditJobDialog";
import { CancelJobDialog, canCancelJob } from "@/components/CancelJobDialog";
import { equipmentSummary } from "@/lib/equipment";

const COLUMNS = [
  { key: "awaiting", title: "Awaiting Dispatch", statuses: ["awaiting_dispatch", "awaiting_parts", "equipment_down", "on_hold"], target: "awaiting_dispatch", tint: "#5B21B6" },
  { key: "scheduled", title: "Scheduled", statuses: ["scheduled"], target: "scheduled", tint: "#94A3B8" },
  { key: "on_site", title: "On Site", statuses: ["on_site"], target: "on_site", tint: "#B45309" },
  { key: "pending_review", title: "Pending Review", statuses: ["tech_complete"], target: "tech_complete", tint: "#0E7490" },
  { key: "complete", title: "Complete Today", statuses: ["complete"], target: "complete", tint: "#15803D" },
];

const JOB_TYPE_LABELS = {
  emergency: "Emergency", repair: "Repair", scheduled_pm: "Scheduled PM",
  warranty: "Warranty", contract: "Contract", quoted_work: "Quoted work",
};

const HOLD_REASONS = [
  { value: "awaiting customer approval", label: "Awaiting customer approval" },
  { value: "awaiting customer deposit", label: "Awaiting customer deposit" },
  { value: "awaiting parts supplier", label: "Awaiting parts supplier" },
  { value: "awaiting site access", label: "Awaiting site access" },
  { value: "other", label: "Other" },
];

const initials = (name = "") => name.split(" ").filter(Boolean).map(w => w[0]).slice(0, 2).join("").toUpperCase();
const firstName = (name = "") => name.split(" ")[0] || "";

function groupVisits(items) {
  const entries = [];
  const byVisit = {};
  items.forEach(job => {
    if (!job.visit_id) { entries.push({ jobs: [job] }); return; }
    if (!byVisit[job.visit_id]) { byVisit[job.visit_id] = { visitId: job.visit_id, jobs: [] }; entries.push(byVisit[job.visit_id]); }
    byVisit[job.visit_id].jobs.push(job);
  });
  entries.forEach(entry => entry.jobs.sort((a, b) => (a.visit_order ?? 0) - (b.visit_order ?? 0)));
  return entries.map(entry => (entry.visitId && entry.jobs.length === 1 ? { jobs: entry.jobs } : entry));
}

function slaInfo(job) {
  if (job.status === "complete") return { label: "DONE", breached: false, soon: false };
  if (job.status === "tech_complete") return { label: "REVIEW", breached: false, soon: false };
  if (job.status === "on_hold") return { label: "ON HOLD", breached: false, soon: false };
  if (!job.sla_due_at) return { label: "—", breached: false, soon: false };
  const ms = new Date(job.sla_due_at) - new Date();
  if (job.sla_excluded) return { label: ms < 0 ? "PM OVERDUE" : `PM ${msToCountdown(ms)}`, breached: false, soon: false };
  return { label: msToCountdown(ms), breached: ms < 0, soon: ms >= 0 && ms < 60 * 60 * 1000 };
}

function JobCard({ job, techs, areas, sites, customers, equipment, onAssign, onMove, onHold, onResume, onApproval, onReschedule, onReview, onDetails, onEdit, onCancel, canDispatch, onDragStart, onDragEnd, dragging, activeScopeSiteIds, activeScopeTechName }) {
  const toLocalInput = value => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = n => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };
  const sla = slaInfo(job);
  const st = job.priority === "equipment_down" && job.status !== "complete" ? "equipment_down" : job.status;
  const meta = STATUS_META[st] || STATUS_META.scheduled;
  const customer = customers.find(c => c.id === job.customer_id);
  const site = sites.find(s => s.id === job.site_id);
  const area = areas.find(a => a.id === job.area_id);
  const tech = techs.find(t => t.id === job.assigned_tech_id);
  const unit = equipment.find(e => e.id === job.equipment_id);
  const outOfScope = activeScopeSiteIds && activeScopeSiteIds.length > 0 && !activeScopeSiteIds.includes(job.site_id);
  const slaClass = sla.breached ? "red" : sla.soon ? "amber" : "";

  return (
    <div
      className={`bf-card ${dragging ? "job-dragging" : ""}`}
      style={{
        opacity: outOfScope ? 0.4 : 1,
        filter: outOfScope ? "grayscale(0.6)" : undefined,
        cursor: canDispatch && !outOfScope ? "grab" : "default",
      }}
      title={outOfScope ? `Outside ${activeScopeTechName}'s site scope` : undefined}
      data-testid={`job-card-${job.ticket_number}`}
      data-outofscope={outOfScope ? "true" : "false"}
      draggable={canDispatch && !outOfScope}
      onDragStart={(e) => { if (outOfScope) { e.preventDefault(); return; } e.dataTransfer.setData("text/plain", job.id); e.dataTransfer.effectAllowed = "move"; onDragStart(job.id); }}
      onDragEnd={onDragEnd}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {canDispatch && !outOfScope && <GripVertical size={12} color="#8A93A1" className="cursor-grab" />}
          <div className="mono text-[12.5px] font-semibold" style={{ color: "#12161B" }}>{job.ticket_number}</div>
          {outOfScope && (
            <Badge className="h-4 px-1.5 text-[9px] rounded-full ml-1"
              style={{ background: "#EEF1F5", color: "#5B6472" }}
              data-testid={`scope-badge-${job.ticket_number}`}>OUT OF SCOPE</Badge>
          )}
        </div>
        <div className={`bf-time-chip ${slaClass}`} data-testid={`sla-${job.ticket_number}`}>
          <Clock size={10} /> {sla.label}
        </div>
      </div>

      <div className="mt-2.5 flex items-center gap-2 flex-wrap">
        <span className="bf-chip" style={{ background: meta.chipBg, color: meta.chipText }} data-testid={`status-chip-${job.ticket_number}`}>
          <span className="bf-dot" style={{ background: meta.chipText }} /> {meta.label}
        </span>
        {job.job_type && (
          <span className="bf-chip" style={{ background: "#EEF1F5", color: "#5B6472" }} data-testid={`job-type-chip-${job.ticket_number}`}>
            {JOB_TYPE_LABELS[job.job_type] || job.job_type}
          </span>
        )}
        {job.added_on_site && (
          <span className="bf-chip" style={{ background: "#E3F2FB", color: "#0B6080" }} data-testid={`added-on-site-${job.ticket_number}`}>
            Added on site · from {job.parent_ticket_number}
          </span>
        )}
        {job.tech_paused && (
          <span className="bf-chip" style={{ background: "#FFF6E5", color: "#96650A" }} data-testid={`tech-paused-${job.ticket_number}`}>
            Paused by technician
          </span>
        )}
      </div>

      <div className="mt-2.5 text-[15px] font-semibold leading-snug" style={{ color: "#12161B", letterSpacing: "-0.012em" }}
           data-testid={`card-customer-${job.ticket_number}`}>
        {customer?.name || "—"}
      </div>
      <div className="text-[12.5px] mt-1" style={{ color: "#5B6472", lineHeight: 1.45 }}>
        {site?.name || "—"} · {area?.name} · {job.description?.slice(0, 70)}{job.description?.length > 70 ? "…" : ""}
      </div>
      {(unit || job.visit_ticket) && (
        <div className="text-[12.5px] mt-1" style={{ color: "#12161B" }} data-testid={`card-equipment-${job.ticket_number}`}>
          {job.visit_ticket ? `Maintenance visit · ${job.unit_count || 0} units` : equipmentSummary(unit)}
        </div>
      )}

      <div className="flex flex-col items-stretch gap-2 mt-3">
        {canDispatch ? (
          <Select value={job.assigned_tech_id || ""} onValueChange={(v) => onAssign(job, v)}>
            <SelectTrigger className="h-9 text-[12.5px] flex-1 rounded-[10px]" data-testid={`assign-${job.ticket_number}`}>
              <SelectValue placeholder="Assign technician" />
            </SelectTrigger>
            <SelectContent>
              {techs.map(t => {
                const scoped = t.site_ids?.length > 0;
                return (
                  <SelectItem key={t.id} value={t.id}>
                    <span className="flex items-center gap-2">
                      <span>{t.name}</span>
                      {scoped && (
                        <span className="mono text-[10px] px-1 rounded"
                          style={{ background: "#FFF6E5", color: "#96650A" }}
                          data-testid={`assign-scope-${t.id}`}
                        >
                          {t.site_ids.length} site{t.site_ids.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        ) : (
          job.assigned_tech_id && tech ? (
            <div className="flex items-center gap-2 text-[12.5px]" style={{ color: "#12161B" }} data-testid={`assigned-tech-${job.ticket_number}`}>
              <span className="bf-avatar">{initials(tech.name)}</span>{firstName(tech.name)}
            </div>
          ) : (
            <div className="text-[12.5px]" style={{ color: "#5B6472" }}>Unassigned</div>
          )
        )}
        {canDispatch && (
          <Select value={job.status} onValueChange={(v) => onMove(job, v)}>
            <SelectTrigger className="h-9 text-[12.5px] w-full rounded-[10px]" data-testid={`status-${job.ticket_number}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="awaiting_dispatch">Awaiting</SelectItem>
              <SelectItem value="awaiting_parts">Awaiting parts</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="on_site">On site</SelectItem>
              <SelectItem value="tech_complete">Pending review</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {canDispatch && job.assigned_tech_id && tech && (
        <div className="flex items-center gap-2 mt-2.5 text-[12.5px]" style={{ color: "#12161B" }} data-testid={`assigned-tech-${job.ticket_number}`}>
          <span className="bf-avatar">{initials(tech.name)}</span>{firstName(tech.name)}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2.5 flex-wrap">
        <span className="bf-time-chip">Created {fmtTimeAST(job.created_at)}</span>
        {job.scheduled_at && <span className="bf-time-chip">Sched {fmtTimeAST(job.scheduled_at)}</span>}
      </div>

      {canDispatch && job.status === "tech_complete" && (
        <Button
          className="mt-3 w-full h-9 text-[12px] bf-btn-primary"
          onClick={() => onReview(job)}
          data-testid={`review-open-${job.ticket_number}`}
        >
          Review &amp; close
        </Button>
      )}

      {canDispatch && !["complete", "tech_complete"].includes(job.status) && (
        <div className="mt-2.5" data-testid={`reschedule-wrap-${job.ticket_number}`}>
          <label className="font-condensed uppercase tracking-wider text-[11px] font-semibold" style={{ color: "#5B6472" }}>
            Reschedule
          </label>
          <input
            type="datetime-local"
            className="mt-1 w-full h-9 rounded-[10px] px-2 text-[12px] bg-white"
            style={{ border: "1px solid #E7EAEE", color: "#12161B" }}
            value={toLocalInput(job.scheduled_at)}
            onChange={event => onReschedule(job, event.target.value)}
            data-testid={`reschedule-${job.ticket_number}`}
          />
        </div>
      )}

      {canDispatch && (
        <div className="mt-3 grid grid-cols-1 gap-2">
          <Button
            variant="outline"
            className="h-9 text-[12px] bf-btn-ghost"
            onClick={() => onDetails(job)}
            data-testid={`details-open-${job.ticket_number}`}
          >
            Times &amp; details
          </Button>
          {job.status !== "complete" && (
            <Button
              variant="outline"
              className="h-9 text-[12px] bf-btn-ghost"
              onClick={() => onEdit(job)}
              data-testid={`edit-job-open-${job.ticket_number}`}
            >
              Edit job
            </Button>
          )}
          <Button
            variant="outline"
            className="h-9 text-[12px] bf-btn-ghost"
            onClick={() => job.status === "on_hold" ? onResume(job) : onHold(job)}
            data-testid={`hold-toggle-${job.ticket_number}`}
          >
            {job.status === "on_hold" ? "Resume" : "Put on hold"}
          </Button>
          <select
            className="h-9 w-full rounded-[10px] px-2 text-[12px] bg-white"
            style={{ border: "1px solid #E7EAEE", color: "#12161B" }}
            value={job.customer_approval?.status || "not required"}
            onChange={event => onApproval(job, event.target.value)}
            data-testid={`approval-${job.ticket_number}`}
          >
            <option value="not required">Approval not required</option>
            <option value="requested">Approval requested</option>
            <option value="received">Approval received</option>
          </select>
          {onCancel && (
            <Button variant="outline" className="h-9 text-[12px] bf-btn-ghost" style={{ color: "#B42318" }}
              onClick={() => onCancel(job)} data-testid={`cancel-job-open-${job.ticket_number}`}>
              Cancel job
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Dispatch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canDispatch = ["service_coordinator", "manager"].includes(user?.role);
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [areas, setAreas] = useState([]);
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [detailJob, setDetailJob] = useState(null);
  const [tick, setTick] = useState(0);
  const [filterTech, setFilterTech] = useState("all");
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [seedAllowed, setSeedAllowed] = useState(false);
  const [holdJob, setHoldJob] = useState(null);
  const [holdReason, setHoldReason] = useState(HOLD_REASONS[2].value);
  const [holdNote, setHoldNote] = useState("");
  const [reviewJob, setReviewJob] = useState(null);
  const [editJob, setEditJob] = useState(null);
  const [cancelJob, setCancelJob] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const isManager = user?.role === "manager";

  const load = async () => {
    const [j, t, a, s, c, e] = await Promise.all([
      client.get("/jobs"), client.get("/technicians"),
      client.get("/areas"), client.get("/sites"), client.get("/customers"), client.get("/equipment"),
    ]);
    setJobs(j.data); setTechs(t.data); setAreas(a.data); setSites(s.data); setCustomers(c.data); setEquipment(e.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    client.get("/seed/status")
      .then(response => setSeedAllowed(Boolean(response.data.allowed)))
      .catch(() => setSeedAllowed(false));
  }, []);
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 30000); return () => clearInterval(id); }, []);

  const seed = async () => {
    const confirm = window.prompt("Type DELETE ALL DATA to reseed sample records.");
    if (confirm !== "DELETE ALL DATA") return;
    try {
      await client.post("/seed", { confirm });
      await load();
      toast.success("Sample data reseeded");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Reseed failed");
    }
  };

  const todayStr = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-GB", { timeZone: "America/Barbados" });
  }, []);

  const filtered = useMemo(() => (
    filterTech === "all" ? jobs : jobs.filter(j => j.assigned_tech_id === filterTech)
  ), [jobs, filterTech]);

  const columns = useMemo(() => COLUMNS.map(col => {
    let items;
    if (col.key === "complete") {
      items = filtered.filter(j => col.statuses.includes(j.status) &&
        j.completed_at && new Date(j.completed_at).toLocaleDateString("en-GB", { timeZone: "America/Barbados" }) === todayStr);
    } else {
      items = filtered.filter(j => col.statuses.includes(j.status));
    }
    items.sort((a, b) => {
      const aBr = !a.sla_excluded && new Date(a.sla_due_at) < new Date();
      const bBr = !b.sla_excluded && new Date(b.sla_due_at) < new Date();
      if (aBr !== bBr) return aBr ? -1 : 1;
      if ((a.priority === "equipment_down") !== (b.priority === "equipment_down")) return a.priority === "equipment_down" ? -1 : 1;
      return new Date(a.sla_due_at) - new Date(b.sla_due_at);
    });
    return { ...col, items };
  }), [filtered, todayStr, tick]);

  const onAssign = async (job, tech_id) => {
    const chosen = techs.find(t => t.id === tech_id);
    if (chosen?.site_ids?.length > 0 && !chosen.site_ids.includes(job.site_id)) {
      toast.error(`${chosen.name} is scoped to specific sites — this job's site is outside their scope.`);
      return;
    }
    const update = { assigned_tech_id: tech_id };
    if (job.status === "awaiting_dispatch" || job.status === "awaiting_parts") {
      update.status = "scheduled";
      const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1);
      update.scheduled_at = d.toISOString();
      const area = areas.find(a => a.id === job.area_id);
      if (!inServiceWindow(area, update.scheduled_at)) {
        toast.warning("Outside service window", { description: `${area?.name} is not workable at that time. You may want to pick a different slot.` });
      }
    }
    await client.patch(`/jobs/${job.id}`, update);
    const tech = techs.find(t => t.id === tech_id);
    toast.success(`Assigned to ${tech?.name}`);
    load();
  };

  const onMove = async (job, status) => {
    try {
      if (status === "complete" && !job.signature) {
        toast.error("Signature required", { description: "Open the job in the technician view to capture a customer signature before closing." });
        return;
      }
      const payload = { status };
      if (status === "scheduled" && !job.scheduled_at) {
        const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1);
        payload.scheduled_at = d.toISOString();
      }
      await client.patch(`/jobs/${job.id}`, payload);
      toast.success(`${job.ticket_number} → ${STATUS_META[status]?.label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const onHold = job => {
    setHoldReason(HOLD_REASONS[2].value);
    setHoldNote("");
    setHoldJob(job);
  };

  const submitHold = async () => {
    if (!holdJob) return;
    try {
      await client.post(`/jobs/${holdJob.id}/hold`, { reason: holdReason, note: holdNote });
      toast.success(`${holdJob.ticket_number} placed on hold`);
      setHoldJob(null);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not hold job");
    }
  };

  const onResume = async job => {
    try {
      await client.post(`/jobs/${job.id}/resume`);
      toast.success(`${job.ticket_number} resumed`);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not resume job");
    }
  };

  const onReschedule = async (job, localValue) => {
    if (!localValue) return;
    const when = new Date(localValue);
    if (Number.isNaN(when.getTime())) return;
    try {
      await client.patch(`/jobs/${job.id}`, { scheduled_at: when.toISOString() });
      const area = areas.find(a => a.id === job.area_id);
      if (!inServiceWindow(area, when.toISOString())) {
        toast.warning("Outside service window", { description: `${area?.name} is not workable at that time. You may want to pick a different slot.` });
      }
      toast.success(`${job.ticket_number} rescheduled to ${fmtTimeAST(when.toISOString())}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reschedule");
    }
  };

  const onApproval = async (job, status) => {
    try {
      await client.patch(`/jobs/${job.id}/approval`, { status });
      toast.success("Customer approval updated");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update approval");
    }
  };

  const onDropToColumn = (col, e) => {
    e.preventDefault();
    setDragOverCol(null);
    const id = e.dataTransfer.getData("text/plain") || dragId;
    if (!id) return;
    const job = jobs.find(j => j.id === id);
    if (!job) return;
    if (col.statuses.includes(job.status)) return; // same column
    onMove(job, col.target);
  };

  const breached = jobs.filter(j => j.status !== "complete" && !j.sla_excluded && new Date(j.sla_due_at) < new Date()).length;
  const parentIds = new Set(jobs.map(j => j.parent_job_id).filter(Boolean));
  const cancelFor = job => (isManager && canCancelJob(job, parentIds.has(job.id)) ? setCancelJob : null);
  const renderCard = j => (
    <JobCard key={j.id} job={j}
      techs={techs} areas={areas} sites={sites} customers={customers} equipment={equipment}
      onAssign={onAssign} onMove={onMove} canDispatch={canDispatch}
      onHold={onHold} onResume={onResume} onApproval={onApproval} onReschedule={onReschedule}
      onReview={setReviewJob}
      onDetails={setDetailJob}
      onEdit={setEditJob}
      onCancel={cancelFor(j)}
      dragging={dragId === j.id}
      onDragStart={(id) => setDragId(id)}
      onDragEnd={() => { setDragId(null); setDragOverCol(null); }}
      activeScopeSiteIds={activeScopeSiteIds}
      activeScopeTechName={scopedTech?.name}
    />
  );
  const scopedTech = filterTech !== "all" ? techs.find(t => t.id === filterTech) : null;
  const activeScopeSiteIds = (scopedTech?.site_ids?.length > 0) ? scopedTech.site_ids : null;

  return (
    <div data-testid="dispatch-page">
      <Toaster richColors position="top-right" />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>Operations</div>
          <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>Dispatch Board</h1>
          <div className="text-[12.5px]" style={{ color: "#5B6472", marginTop: "4px" }}>
            {jobs.length} active tickets · <span style={{ color: breached ? "#B42318" : "#136B36" }} className="font-medium">{breached} SLA breached</span>
            {canDispatch && <span className="ml-2 mono text-[11px] px-2 py-0.5 rounded-full" style={{ background: "#EAF0FE", color: "#2563EB" }}>Drag cards between columns</span>}
            {scopedTech && activeScopeSiteIds && (
              <span className="ml-2 mono text-[11px] px-2 py-0.5 rounded-full" style={{ background: "#FFF6E5", color: "#96650A" }} data-testid="scope-filter-badge">
                Scope: {scopedTech.name} · {activeScopeSiteIds.length} site{activeScopeSiteIds.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterTech} onValueChange={setFilterTech}>
            <SelectTrigger className="w-[180px] h-9 rounded-[10px]" data-testid="tech-filter">
              <SelectValue placeholder="All technicians" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All technicians</SelectItem>
              {techs.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {seedAllowed && (
            <Button variant="outline" className="h-9 bf-btn-ghost" onClick={seed} data-testid="seed-btn">
              Reseed sample
            </Button>
          )}
          <Button className="h-9 px-4 bf-btn-primary" onClick={() => navigate("/intake")} data-testid="new-ticket-btn">
            + New service request
          </Button>
        </div>
      </div>

      {breached > 0 && (
        <div className="mt-4 flex items-start gap-2 p-3.5 rounded-[14px]"
          style={{ background: "#FDECEC" }}>
          <AlertTriangle size={18} color="#B42318" className="mt-0.5" />
          <div className="text-[12.5px]" style={{ color: "#B42318" }}>
            <strong>{breached}</strong> job{breached > 1 ? "s" : ""} past SLA. Equipment-down tickets are shown first.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3.5 mt-4" data-testid="columns-grid">
        {columns.map(col => (
          <div key={col.key}
            className={`bf-col ${dragOverCol === col.key ? "col-drop-target" : ""}`}
            data-testid={`column-${col.key}`}
            onDragOver={canDispatch ? (e) => { e.preventDefault(); setDragOverCol(col.key); } : undefined}
            onDragLeave={() => setDragOverCol(c => c === col.key ? null : c)}
            onDrop={canDispatch ? (e) => onDropToColumn(col, e) : undefined}
          >
            <div className="px-1 py-1 mb-2.5 flex items-center justify-between">
              <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
                {col.title}
              </div>
              <span className="bf-count-pill">{col.items.length}</span>
            </div>
            <div className="space-y-2.5 min-h-[200px]">
              {col.items.length === 0 && (
                <div className="text-center text-[12.5px] py-10" style={{ color: "#8A93A1" }}>
                  {canDispatch ? "Drop a card here." : "Nothing here."}
                </div>
              )}
              {groupVisits(col.items).map(entry => entry.visitId ? (
                <div key={entry.visitId} className="rounded-[14px] p-2 space-y-2" style={{ background: "#EEF2F8" }}
                     data-testid={`visit-card-${entry.visitId}`}>
                  <div className="px-1 pt-1 text-[12.5px] font-semibold" style={{ color: "#1E3A8A" }}>
                    Visit — {customers.find(c => c.id === entry.jobs[0].customer_id)?.name},{" "}
                    {sites.find(s => s.id === entry.jobs[0].site_id)?.name} — {entry.jobs.length} units
                  </div>
                  {entry.jobs.map(j => renderCard(j))}
                </div>
              ) : renderCard(entry.jobs[0]))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!holdJob} onOpenChange={value => { if (!value) setHoldJob(null); }}>
        <DialogContent className="max-w-[460px]" data-testid="hold-reason-dialog">
          <DialogHeader>
            <DialogTitle>Put {holdJob?.ticket_number} on hold</DialogTitle>
            <DialogDescription>Pick the reason this job is waiting.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {HOLD_REASONS.map(reason => (
              <label
                key={reason.value}
                className="flex items-center gap-3 rounded-[12px] px-3 cursor-pointer"
                style={{ background: holdReason === reason.value ? "#EAF0FE" : "#F1F3F6", minHeight: 44 }}
                data-testid={`hold-reason-option-${reason.value.replace(/ /g, "-")}`}
              >
                <input
                  type="radio"
                  name="hold-reason"
                  value={reason.value}
                  checked={holdReason === reason.value}
                  onChange={() => setHoldReason(reason.value)}
                  className="h-4 w-4 accent-[#2563EB]"
                />
                <span className="text-[13.5px]" style={{ color: holdReason === reason.value ? "#1E3A8A" : "#12161B" }}>
                  {reason.label}
                </span>
              </label>
            ))}
          </div>
          {holdReason === "other" && (
            <div className="mt-1">
              <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold mb-1" style={{ color: "#5B6472" }}>
                Note (optional)
              </div>
              <Textarea
                rows={2}
                className="rounded-[10px] text-[13.5px]"
                value={holdNote}
                onChange={event => setHoldNote(event.target.value)}
                placeholder="What is this job waiting on?"
                data-testid="hold-note-input"
              />
            </div>
          )}
          <DialogFooter className="mt-3">
            <Button variant="outline" className="bf-btn-ghost" onClick={() => setHoldJob(null)} data-testid="hold-cancel-btn">
              Cancel
            </Button>
            <Button className="bf-btn-primary px-4" onClick={submitHold} data-testid="hold-confirm-btn">
              Put on hold
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <JobTimesDialog job={detailJob} onClose={() => setDetailJob(null)} onDone={load}
        onCancel={detailJob && cancelFor(detailJob) ? () => { setCancelJob(detailJob); setDetailJob(null); } : null} />
      <CancelJobDialog job={cancelJob} onClose={() => setCancelJob(null)} onDone={() => { setCancelJob(null); load(); }} />

      <JobReviewDialog
        job={reviewJob}
        onClose={() => setReviewJob(null)}
        onDone={() => { setReviewJob(null); load(); }}
      />
      <EditJobDialog job={editJob} techs={techs} onClose={() => setEditJob(null)} onDone={() => { setEditJob(null); load(); }} />
    </div>
  );
}


